import { useCallback } from "react";

import { useJitsu } from "@jitsu/react";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useRecordSocraticTurnPreparationMediaMutation } from "@/generated/graphql";

interface Props {
  workId: string;
  turnId: string | undefined;
  dialogueId: string;
}

export interface RecordPreparationMediaHandlers {
  /** Records the uploaded media against the turn once an upload completes. */
  onUploaded: (mediaId: string, mediaUrl: string, durationSeconds: number) => void;
  /** Reports a failed preparation-media upload for observability. */
  onUploadFailed: (error: unknown) => void;
}

/**
 * Hook that fires the recordPreparationMedia mutation once the preparation
 * phase video has been uploaded. Fire-and-forget — students never see the
 * result. Failures at either stage (the upload, a rejected mutation, or a
 * non-null payload `error`) are tracked for observability rather than surfaced
 * to the student, tagged with the `stage` at which they occurred.
 */
export function useRecordPreparationMedia({
  workId,
  turnId,
  dialogueId,
}: Props): RecordPreparationMediaHandlers {
  const { track } = useJitsu();
  const [recordPreparationMediaMutation] = useRecordSocraticTurnPreparationMediaMutation();

  const trackFailure = useCallback(
    (stage: "upload" | "record", error: unknown) => {
      track(JitsuEvent.VIDEO_RESPONSE_PREPARATION_MEDIA_RECORD_FAILED, {
        dialogue_id: dialogueId,
        stage,
        error_message: error instanceof Error ? error.message : String(error),
      });
    },
    [track, dialogueId]
  );

  const onUploaded = useCallback(
    (mediaId: string, mediaUrl: string, durationSeconds: number) => {
      if (turnId == null) return;
      void recordPreparationMediaMutation({
        variables: {
          workId,
          input: { turnId, mediaId, mediaUrl, durationSeconds },
        },
      })
        .then((result) => {
          const error = result.data?.recordSocraticTurnPreparationMedia.error;
          if (error != null) trackFailure("record", error);
        })
        .catch((e) => trackFailure("record", e));
    },
    [turnId, workId, recordPreparationMediaMutation, trackFailure]
  );

  const onUploadFailed = useCallback(
    (error: unknown) => trackFailure("upload", error),
    [trackFailure]
  );

  return { onUploaded, onUploadFailed };
}
